import {DataEditorRef, Item, Item as GridItem} from "@glideapps/glide-data-grid";
import {DispatchRequestEntity, dispatchRequestStatus} from "@ots-frontend/ots-data";
import {forwardRef, useCallback, useEffect, useState} from "react";
import {Theme} from "@glideapps/glide-data-grid/dist/ts/common/styles";
import {CellClickedEventArgs} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-types";
import {useLayer} from "react-laag";
import {Menu} from "@mantine/core";
import {GridCellContextMenu, isSizedGridColumn} from "../DataGrid/useDataGrid";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
  DispatchRequestDispatchAction,
  DispatchRequestDispatchActionMenuDropdown
} from "../../menu/DispatchRequestActionMenu/DispatchRequestDispatchActionMenu";
import {IconEye} from "@tabler/icons-react";
import {EMPTY_ARRAY, useDispatchRequestDataGrid} from "./useDispatchRequestDataGrid";
import {ThemedDataGrid} from "../DataGrid/ThemedDataGrid";
import {EntityDataGridProps} from "../EntityDataGrid/EntityDataGrid";

export type DispatchRequestDataGridProps = EntityDataGridProps<DispatchRequestEntity> & {
  onActionClick?: (data: DispatchRequestEntity, action: DispatchRequestDispatchAction) => void;
}

export const DispatchRequestDataGrid = forwardRef<DataEditorRef, DispatchRequestDataGridProps>((props, ref) => {
  const {
    data = EMPTY_ARRAY,
    onActionClick,
    onCellClicked,
    colWidths,
    onColumnResize,
    sort,
    onSort,
    ...rest
  } = props;

  const [contextMenu, setContextMenu] = useState<GridCellContextMenu<DispatchRequestEntity>>();

  const dataGridArgs = useDispatchRequestDataGrid({
    data: data,
    colWidths: colWidths,
    sort: sort,
  });

  const {
    getOriginalIndex
  } = dataGridArgs;

  useEffect(() => {
    if (onColumnResize) {
      const widths = dataGridArgs.columns.map((c) => {
        if (isSizedGridColumn(c)) {
          return c.width
        } else {
          return undefined
        }
      })
      onColumnResize(widths)
    }
  }, [dataGridArgs.columns, onColumnResize]);

  const isOpen = typeof contextMenu !== "undefined";

  const {layerProps, renderLayer} = useLayer({
    isOpen: isOpen,
    auto: true,
    placement: "bottom-end",
    triggerOffset: 2,
    onOutsideClick: () => setContextMenu(undefined),
    trigger: {
      getBounds: () => ({
        left: contextMenu?.bounds.x ?? 0,
        top: contextMenu?.bounds.y ?? 0,
        width: contextMenu?.bounds.width ?? 0,
        height: contextMenu?.bounds.height ?? 0,
        right: (contextMenu?.bounds.x ?? 0) + (contextMenu?.bounds.width ?? 0),
        bottom: (contextMenu?.bounds.y ?? 0) + (contextMenu?.bounds.height ?? 0)
      })
    }
  });

  const onCellContextMenu = useCallback((cell: GridItem, event: CellClickedEventArgs) => {
    event.preventDefault();
    const row = getOriginalIndex(cell[1]);
    setContextMenu({
      col: cell[0],
      row: row,
      bounds: event.bounds,
      data: data[row]
    });
  }, [data, getOriginalIndex]);

  const onContextMenuItemClick = useCallback((data: DispatchRequestEntity, action: DispatchRequestDispatchAction) => {
    if (onActionClick) {
      onActionClick(data, action);
    }
    setContextMenu(undefined)
  }, [onActionClick]);

  const getRowThemeOverride = useCallback((row: number): Partial<Theme> | undefined => {
    const dispatchRequest = data[getOriginalIndex(row)];

    if (dispatchRequest) {
      switch (dispatchRequest.status) {
        case dispatchRequestStatus.canceled:
        case dispatchRequestStatus.rejected:
        case dispatchRequestStatus.reassigned:
        case dispatchRequestStatus.unresolved:
          return {
            bgCell: "#e6b8b7"
          };
        default:
          break;
      }

      if (dispatchRequest.status === dispatchRequestStatus.pending) {
        return {
          bgCell: "#fb8a51"
        };
      }

      if (dispatchRequest.status === dispatchRequestStatus.completed) {
        return {
          bgCell: "#d8e4bc"
        };
      }

      if (dispatchRequest.status === dispatchRequestStatus.pickedUp) {
        return {
          bgCell: "#fafa93"
        };
      }
    }

    return undefined;
  }, [data, getOriginalIndex]);

  const _onCellClicked = useCallback((cell: Item, event: CellClickedEventArgs) => {
    if (onCellClicked) {
      onCellClicked(cell, event)
    }
  }, [onCellClicked])

  useEffect(() => {
    if (onSort) {
      onSort(dataGridArgs.sort)
    }
  }, [onSort, dataGridArgs.sort])

  return (
    <>
      <ThemedDataGrid
        {...rest}
        {...dataGridArgs}
        ref={ref}
        width="100%"
        smoothScrollX={true}
        smoothScrollY={true}
        rowMarkers="number"
        getCellsForSelection={true}
        onCellClicked={onCellClicked ? _onCellClicked : undefined}
        getRowThemeOverride={getRowThemeOverride}
        onCellContextMenu={onCellContextMenu}/>
      {(isOpen && contextMenu) &&
        renderLayer(
          <div {...layerProps}>
            <ContextMenu {...contextMenu} onActionClick={onContextMenuItemClick}/>
          </div>
        )
      }
    </>
  );
});

export type DispatchRequestDataGridContextMenuItem = "ACCEPT" | "REJECT"

type ContextMenuProps = GridCellContextMenu<DispatchRequestEntity> & {
  onActionClick: (data: DispatchRequestEntity, action: DispatchRequestDispatchAction) => void
}

const ContextMenu = (props: ContextMenuProps) => {
  const {data, onActionClick} = props;

  const {t} = useTranslation()
  const navigate = useNavigate()

  return (
    <Menu shadow="md" width={200} opened={true}>
      <Menu.Dropdown>
        <Menu.Item
          icon={<IconEye size={14}/>}
          onClick={() => navigate(`/requests/profile?id=${data.id}`)}>
          {t("common.action.view", "View")}
        </Menu.Item>
        <DispatchRequestDispatchActionMenuDropdown
          dispatchRequest={data}
          onActionClick={(action) => onActionClick(data, action)}
          showEmpty={false}/>
      </Menu.Dropdown>
    </Menu>
  );
};
